import {
  MEDIA_QUERY_DEVICE_SIZE,
  MEDIA_QUERY_SOURCE,
  MEDIA_QUERY_SOURCE_IMAGE_WIDTH,
} from '@/app/components/media/image/constants';
import type {
  ImageOptions,
  ImageSource,
} from '@/app/components/media/image/types';

type ImageOptionsWithoutWidth = Omit<ImageOptions, 'width'>;

interface DesktopSource {
  desktopSrc?: string;
  desktopOptions?: ImageOptionsWithoutWidth;
}

interface MobileSource {
  mobileSrc?: string;
  mobileOptions?: ImageOptionsWithoutWidth;
}

interface ResponsiveSource extends DesktopSource, MobileSource {}

/**
 * Generate sources order should be done manually. Ordered by screen size in decending
 */
type GeneralSourcesConfig =
  | 'desktop'
  | 'lgDesktop'
  | 'tablet'
  | 'mobile'
  | 'overall'
  | 'overallLgDesktop';

/**
 * General generate sources
 */
const getDesktopSources = (src: DesktopSource): ImageSource[] => {
  const { desktopSrc, desktopOptions } = src;
  if (!desktopSrc) return [];

  return [
    {
      srcset: desktopSrc!,
      media: MEDIA_QUERY_SOURCE.desktop_xl, // min-width: 1920
      options: {
        ...desktopOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.desktop_xl,
      },
    },
    {
      srcset: desktopSrc!,
      media: MEDIA_QUERY_SOURCE.desktop_lg, // minWidth: 1600, maxWidth: 1919
      options: {
        ...desktopOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.desktop_lg,
      },
    },
    {
      srcset: desktopSrc!,
      media: MEDIA_QUERY_SOURCE.desktop_md, // minWidth: 1440, maxWidth: 1599
      options: {
        ...desktopOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.desktop_md,
      },
    },
    {
      srcset: desktopSrc!,
      media: MEDIA_QUERY_SOURCE.desktop_sm, // minWidth: 1280, maxWidth: 1439
      options: {
        ...desktopOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.desktop_sm,
      },
    },
  ];
};

const getLgDesktopSources = (src: ResponsiveSource): ImageSource[] => {
  const { mobileSrc, mobileOptions, desktopSrc, desktopOptions } = src;
  if (!mobileSrc || !desktopSrc) return [];

  return [
    {
      srcset: desktopSrc!,
      media: MEDIA_QUERY_SOURCE.tablet_lg,
      options: {
        ...desktopOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_lg,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.tablet_md,
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_md,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.tablet_sm,
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_sm,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.mobile_lg,
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.mobile_lg,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.tablet_lg,
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_lg,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.tablet_md,
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_md,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.tablet_sm,
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_sm,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.mobile_lg,
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.mobile_lg,
      },
    },
  ];
};

const getTabletSources = (src: MobileSource): ImageSource[] => {
  const { mobileSrc, mobileOptions } = src;
  if (!mobileSrc) return [];

  return [
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.tablet_lg,
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_lg,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.tablet_md,
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_md,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.tablet_sm,
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_sm,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.mobile_lg,
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.mobile_lg,
      },
    },
  ];
};

const getMobileSources = (source: MobileSource): ImageSource[] => {
  const { mobileSrc, mobileOptions } = source;
  if (!mobileSrc) return [];

  return [
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.mobile_md, // minWidth: 390, maxWidth: 479
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.mobile_md,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.mobile_sm, // minWidth: 321, maxWidth: 389
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.mobile_sm,
      },
    },
    {
      srcset: mobileSrc!,
      media: MEDIA_QUERY_SOURCE.mobile_xs, // maxWidth: 320
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.mobile_xs,
      },
    },
  ];
};

export const generateSources = (
  config: GeneralSourcesConfig,
  source: ResponsiveSource
): ImageSource[] => {
  if (!source || !config) return [];

  switch (config) {
    case 'desktop':
      return getDesktopSources(source);
    case 'lgDesktop':
      return getLgDesktopSources(source);
    case 'tablet':
      return getTabletSources(source);
    case 'mobile':
      return getMobileSources(source);
    case 'overall':
      return [
        ...getDesktopSources(source),
        ...getTabletSources(source),
        ...getMobileSources(source),
      ];
    case 'overallLgDesktop':
      return [
        ...getDesktopSources(source),
        ...getLgDesktopSources(source),
        ...getMobileSources(source),
      ];
    default:
      return [];
  }
};

/**
 * Custom generate sources
 */
export const generateSourcesSplash = (
  source: ResponsiveSource
): ImageSource[] => {
  const { desktopSrc, desktopOptions, mobileSrc, mobileOptions } = source;
  if (!desktopSrc || !mobileSrc) return [];

  return [
    ...getDesktopSources(source),
    /** Desktop and IPad Pro */
    {
      srcset: desktopSrc!,
      media: {
        ...MEDIA_QUERY_SOURCE.tablet_lg,
        orientation: 'landscape',
        device: 'desktop',
      }, // minWidth: 1024, maxWidth: 1279, orientation: 'landscape'
      options: {
        ...desktopOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_lg,
      },
    },
    {
      srcset: mobileSrc!,
      media: {
        minWidth: MEDIA_QUERY_DEVICE_SIZE.tablet_md,
        maxWidth: MEDIA_QUERY_DEVICE_SIZE.desktop_md - 1,
        orientation: 'portrait',
        device: 'mobile',
      }, // minWidth: 1024, maxWidth: 1439, orientation: 'portrait'
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_lg,
      },
    },
    /** Tablet: Handle Portrait and Landscape */
    {
      srcset: desktopSrc!,
      media: {
        ...MEDIA_QUERY_SOURCE.mobile_lg,
        maxWidth: MEDIA_QUERY_DEVICE_SIZE.tablet_md - 1,
        orientation: 'landscape',
        device: 'desktop',
      }, // minWidth: 480, maxWidth: 1023, orientation: 'landscape'
      options: {
        ...desktopOptions,
        width: 750,
      },
    },
    {
      srcset: mobileSrc!,
      media: {
        ...MEDIA_QUERY_SOURCE.tablet_md,
        orientation: 'portrait',
      }, // minWidth: 768, maxWidth: 1023, orientation: 'portrait'
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_md,
      },
    },
    {
      srcset: mobileSrc!,
      media: {
        ...MEDIA_QUERY_SOURCE.tablet_sm,
        orientation: 'portrait',
      }, // minWidth: 576, maxWidth: 767, orientation: 'portrait'
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.tablet_sm,
      },
    },
    {
      srcset: mobileSrc!,
      media: {
        ...MEDIA_QUERY_SOURCE.mobile_lg,
        orientation: 'portrait',
      }, // minWidth: 480, maxWidth: 575, orientation: 'portrait'
      options: {
        ...mobileOptions,
        width: MEDIA_QUERY_SOURCE_IMAGE_WIDTH.mobile_lg,
      },
    },
    ...getMobileSources(source),
  ];
};
